/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

export const NOTIFYER_PIPEDRIVE_URL = "https://notifyer.whatsable.app/pipedrive";
export const NOTIFYER_WHATSAPP_CONNECTION_URL =
  "https://notifyer.whatsable.app/onboarding/whatsapp-connection";

export type LoginSessionReject =
  | { type: "authorization"; message?: string }
  | { type: "error"; message?: string };

interface AuthState {
  loading?: boolean;
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  isCheckingWhatsApp: boolean;
  whatsappChecked: boolean;
  authorizationRequired: boolean;
  whatsappConnectionRequired: boolean;
  isSubscribed: boolean;
  subscriptionMessage: string | null;
  error: string | boolean | undefined;
  system_user_id: string | null;
  labels: Label[];
  token : string
}
export interface Label {
  id: number;
  label: string;
  user_id: string;
  keywords: string[];
  created_at: number;
}

const initialState: AuthState = {
  isAuthenticated: false,
  isAuthenticating: false,
  isCheckingWhatsApp: false,
  whatsappChecked: false,
  authorizationRequired: false,
  whatsappConnectionRequired: false,
  isSubscribed: true,
  subscriptionMessage: null,
  loading: false,
  error: false,
  system_user_id: null,
  labels: [],
  token : ""
};

export const fetchLabels = createAsyncThunk(
  "auth/fetchLabels",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as any;
      const token = state.auth.token;
      
      const response = await axios.get(
        "https://api.insightssystem.com/api:uYdlVwac/web/label_management",
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: token })
          }
        }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "Something went wrong");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

export const loginSession = createAsyncThunk(
  "auth/loginSession",
  async ({ userId, companyId }: { userId: string; companyId: string }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        'https://api.insightssystem.com/api:uYdlVwac/pipedrive/chat/session',
        {
          pdUserId: userId,
          companyId
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const { success, token, systemUserId } = response.data;

      if (success === false) {
        return rejectWithValue({
          type: "authorization",
        } satisfies LoginSessionReject);
      }

      if (!token) {
        return rejectWithValue({
          type: "error",
          message: "Login session failed",
        } satisfies LoginSessionReject);
      }

      return {
        success: true,
        token,
        systemUser: systemUserId,
      };

    } catch (error) {
      if (axios.isAxiosError(error)) {
        const data = error.response?.data;
        if (data?.success === false) {
          return rejectWithValue({
            type: "authorization",
          } satisfies LoginSessionReject);
        }
      }
      return rejectWithValue({
        type: "error",
        message: "Login session failed",
      } satisfies LoginSessionReject);
    }
  }
);

export const checkWhatsAppNumber = createAsyncThunk(
  "auth/checkWhatsAppNumber",
  async (
    { userId, companyId }: { userId: string; companyId: string },
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as any;
      const token = state.auth.token;

      const response = await axios.post(
        "https://api.insightssystem.com/api:uYdlVwac/pipedrive/chat/check/whatsapp/number",
        {
          pdUserId: userId,
          companyId,
        },
        {
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: token }),
          },
        }
      );

      const { success, is_subscribe, subscription_message } = response.data;

      // Step 1: WhatsApp number must be connected
      if (success === false) {
        return rejectWithValue({ type: "whatsapp" as const });
      }

      // Step 2: WhatsApp connected — check subscription (both cases proceed to app)
      return {
        connected: true,
        isSubscribed: is_subscribe === true,
        subscriptionMessage: subscription_message ?? null,
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const data = error.response?.data;
        if (data?.success === false) {
          return rejectWithValue({ type: "whatsapp" as const });
        }
      }
      return rejectWithValue({
        type: "error" as const,
        message: "Could not verify WhatsApp connection",
      });
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    login(state, action: PayloadAction<{ system_user_id: string }>) {
      state.isAuthenticated = true;
      state.system_user_id = action.payload.system_user_id;
    },
    logout(state) {
      state.isAuthenticated = false;
      state.system_user_id = null;
    },
    addLabel(state, action: PayloadAction<Label>) {
      state.labels.push(action.payload);
    },
    removeLabel(state, action: PayloadAction<number>) {
      state.labels = state.labels.filter(
        (label) => label.id !== action.payload
      );
    },
    addKeywordToLabel(
      state,
      action: PayloadAction<{ labelId: number; newKeyword: string }>
    ) {
      const { labelId, newKeyword } = action.payload;
      const label = state.labels.find((label) => label.id === labelId);
      if (label) {
        label.keywords.push(newKeyword);
      }
    },
    removeKeywordFromLabel(
      state,
      action: PayloadAction<{ labelId: number; removedKeyword: string }>
    ) {
      const { labelId, removedKeyword } = action.payload;
      const label = state.labels.find((label) => label.id === labelId);

      if (label) {
        label.keywords = label.keywords.filter(
          (keyword) => keyword !== removedKeyword
        );
      }
    },
    setIsAuthenticating: (state, action) => {
      state.isAuthenticating = action.payload;
    },
    setIsAuthenticated: (state, action) => {
      state.isAuthenticated = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLabels.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchLabels.fulfilled,
        (state, action: PayloadAction<Label[]>) => {
          state.loading = false;
          state.labels = action.payload;
        }
      )
      .addCase(fetchLabels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(loginSession.pending, (state) => {
        state.isAuthenticating = true;
        state.error = false;
        state.authorizationRequired = false;
        state.whatsappConnectionRequired = false;
        state.whatsappChecked = false;
        state.isCheckingWhatsApp = false;
        state.isSubscribed = true;
        state.subscriptionMessage = null;
      })
      .addCase(loginSession.fulfilled, (state, action) => {
        state.isAuthenticating = false;
        state.isAuthenticated = false;
        state.authorizationRequired = false;
        state.error = false;
        state.token = action.payload.token;
        state.system_user_id = action.payload.systemUser || null;
      })
      .addCase(loginSession.rejected, (state, action) => {
        const payload = action.payload as LoginSessionReject | undefined;
        state.isAuthenticating = false;
        state.isAuthenticated = false;
        state.authorizationRequired = payload?.type === "authorization";
        state.whatsappConnectionRequired = false;
        state.whatsappChecked = false;
        state.error = payload?.message ?? "Login session failed";
        state.token = "";
        state.system_user_id = null;
      })
      .addCase(checkWhatsAppNumber.pending, (state) => {
        state.isCheckingWhatsApp = true;
        state.error = false;
      })
      .addCase(checkWhatsAppNumber.fulfilled, (state, action) => {
        state.isCheckingWhatsApp = false;
        state.whatsappChecked = true;
        state.whatsappConnectionRequired = false;
        state.isSubscribed = action.payload.isSubscribed;
        state.subscriptionMessage = action.payload.subscriptionMessage;
        state.isAuthenticated = true;
      })
      .addCase(checkWhatsAppNumber.rejected, (state, action) => {
        const payload = action.payload as
          | { type: "whatsapp" }
          | { type: "error"; message?: string }
          | undefined;
        state.isCheckingWhatsApp = false;
        state.whatsappChecked = true;
        state.isAuthenticated = false;
        if (payload?.type === "whatsapp") {
          state.whatsappConnectionRequired = true;
          state.error = false;
        } else {
          state.whatsappConnectionRequired = false;
          state.error =
            payload && "message" in payload
              ? payload.message ?? "Could not verify WhatsApp connection"
              : "Could not verify WhatsApp connection";
        }
      });
  },
});

export const {
  login,
  logout,
  addLabel,
  removeLabel,
  addKeywordToLabel,
  removeKeywordFromLabel,
  setIsAuthenticating,
  setIsAuthenticated
} = authSlice.actions;
export const authReducer = authSlice.reducer;
